import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../../utils/api';
import { STATUS } from '../../../utils/status';
import { AnalyticsExpertState, AnalyticsPayload } from '../../interfaces/interface';
import extraReducers from '../../extraReducers/analyticsExpertExtraReducer';

const initialState: AnalyticsExpertState = {
  analyticsExpert: {
    count: undefined,
    results: []
  },
  error: null,
  getStatus: STATUS.NOT_STARTED,
  downloadAnalyticsByExpertStatus: STATUS.NOT_STARTED,
  analyticsExpertDownload: '',
  getExpertAnalyticsMessage: "",
  getExpertAnalyticsDownloadMessage: ""
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toPercentage = (numerator: number, denominator: number): number => {
  if (denominator <= 0) {
    return 0;
  }
  return Number(((numerator / denominator) * 100).toFixed(2));
};

const normalizeExpertRow = (row: Record<string, any>) => {
  const acceptedAsIs = toNumber(
    row?.total_quality_accepted_as_is ??
    row?.quality_accepted_as_is_tasks ??
    row?.accepted_as_is_count ??
    0
  );
  const acceptedWithEdits = toNumber(
    row?.total_quality_accepted_with_edits ??
    row?.quality_accepted_with_edits_tasks ??
    row?.accepted_with_edits_count ??
    0
  );
  const rejected = toNumber(
    row?.total_quality_rejected ??
    row?.quality_rejected_tasks ??
    row?.rejected_count ??
    0
  );

  return {
    ...row,
    total_quality_accepted_as_is: acceptedAsIs,
    total_quality_accepted_with_edits: acceptedWithEdits,
    total_quality_rejected: rejected,
    total_accepted: acceptedAsIs + acceptedWithEdits,
    accepted_count: acceptedAsIs + acceptedWithEdits,
    total_rejected: rejected,
    rejected_count: rejected,
  };
};

export const getExpertAnalytics = createAsyncThunk(
  'project/qa-analytics/overview/taskbyexpert/get',
  async (payload: AnalyticsPayload = { association_type: '' }, thunkAPI) => {
    try {
      const {
        search,
        page,
        pageSize,
        sort_by,
        sort_order,
        project_id,
        association_type,
        download,
        chip,
        tab,
        stage_id,
        error_type_sort_by,
        error_type_sort_order,
        granularity,
        start_date,
        end_date,
      } = payload;
      const queryString = new URLSearchParams({
        ...(search && { search }),
        ...(page && { page: page.toString() }),
        ...(pageSize && { page_size: pageSize.toString() }),
        ...(sort_by && { sort_by }),
        ...(sort_order && { sort_order }),
        ...(stage_id && { stage_id: String(stage_id) }),
        ...(error_type_sort_by && { error_type_sort_by }),
        ...(error_type_sort_order && { error_type_sort_order }),
        ...(granularity && { granularity }),
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        ...(project_id && { project_id }),
        tab: tab || 'experts',
        ...(chip && { chip }),
        ...(association_type && { association_type }),
        ...(download != undefined && { download: download.toString() }),
      }).toString();
      const response = await api.get(`project/qa-analytics/overview/?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        const mappedData = data?.data || data;
        const normalizedResults = Array.isArray(mappedData?.results)
          ? mappedData.results.map((row: Record<string, any>) => normalizeExpertRow(row))
          : [];
        const acceptedAsIsCount = toNumber(
          mappedData?.accepted_as_is_count ??
          mappedData?.quality_accepted_as_is_tasks ??
          0
        );
        const acceptedWithEditsCount = toNumber(
          mappedData?.accepted_with_edits_count ??
          mappedData?.quality_accepted_with_edits_tasks ??
          0
        );
        const rejectedCount = toNumber(
          mappedData?.rejected_total_count ??
          mappedData?.quality_rejected_total_tasks ??
          mappedData?.rejected_count ??
          0
        );
        const reviewedTotal = toNumber(mappedData?.total_reviewed ?? acceptedAsIsCount + acceptedWithEditsCount + rejectedCount);
        const acceptedAsIsPercentage = toNumber(
          mappedData?.accepted_as_is_percentage ??
          mappedData?.quality_accepted_as_is_percentage ??
          toPercentage(acceptedAsIsCount, reviewedTotal)
        );
        const acceptedWithEditsPercentage = toNumber(
          mappedData?.accepted_with_edits_percentage ??
          mappedData?.quality_accepted_with_edits_percentage ??
          toPercentage(acceptedWithEditsCount, reviewedTotal)
        );
        const rejectedPercentage = toNumber(
          mappedData?.rejected_percentage ??
          mappedData?.quality_rejected_percentage ??
          toPercentage(rejectedCount, reviewedTotal)
        );

        return {
          count: mappedData?.count ?? 0,
          results: normalizedResults,
          accepted_percentage: mappedData?.accepted_percentage ?? acceptedAsIsPercentage + acceptedWithEditsPercentage,
          accepted_as_is_percentage: acceptedAsIsPercentage,
          accepted_with_edits_percentage: acceptedWithEditsPercentage,
          rejected_percentage: rejectedPercentage,
          accepted_count: toNumber(mappedData?.accepted_count ?? acceptedAsIsCount + acceptedWithEditsCount),
          accepted_as_is_count: acceptedAsIsCount,
          accepted_with_edits_count: acceptedWithEditsCount,
          rejected_count: rejectedCount,
          total_reviewed: reviewedTotal,
          top_error_type: mappedData?.top_error_type ?? '',
          error_type_breakdown: mappedData?.error_type_breakdown || [],
        };
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = `Request failed with status ${status}`;
        if (data && data.message) {
          errorMessage += `: ${data.message}`;
          console.log({ errorMessage });
        }
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

export const getExpertAnalyticsDownload = createAsyncThunk(
  '/task/company/analytics/by_expert/get/download',
  async (payload: AnalyticsPayload = { association_type: '' }, thunkAPI) => {
    try {
      const {
        search,
        page,
        pageSize,
        sort_by,
        sort_order,
        project_id,
        type,
        association_type,
        download,
        chip,
        tab,
        stage_id,
        granularity,
        start_date,
        end_date,
      } = payload;
      const queryString = new URLSearchParams({
        ...(search && { search }),
        ...(page && { page: page.toString() }),
        ...(pageSize && { page_size: pageSize.toString() }),
        ...(sort_by && { sort_by }),
        ...(sort_order && { sort_order }),
        ...(project_id && { project_id }),
        ...(type && { type }),
        ...(chip && { chip }),
        ...(tab && { tab }),
        ...(stage_id && { stage_id: String(stage_id) }),
        ...(granularity && { granularity }),
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        include_split_status: 'true',
        ...(association_type && { association_type }),
        ...(download != undefined && { download: download.toString() }),
      }).toString();
      console.log(type)
      const response = await api.get(`task/company/analytics/download_tasks_by_experts?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = `Request failed with status ${status}`;
        if (data && data.message) {
          errorMessage += `: ${data.message}`;
          console.log({ errorMessage });
        }
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

const analyticsExpertsSlice = createSlice({
  name: 'analyticsExpert',
  initialState,
  reducers: {
    setDownloadAnalyticsByExpertStatus: (state, actions) => {
      state.downloadAnalyticsByExpertStatus = actions.payload
    }
  },
  extraReducers
});

export const { setDownloadAnalyticsByExpertStatus } = analyticsExpertsSlice.actions
export default analyticsExpertsSlice.reducer;
